import { SlidingFixedChild, SlidingFixedParent } from '@rcm/react-sliding-fixed';
import classNames from 'classnames';
import React, { ComponentType, CSSProperties, ReactNode } from 'react';
import LoadingIndicator from './LoadingIndicator';
import './LoadingIndicatorOverlay.scss';

type Props<
  P extends {
    style?: CSSProperties;
    children: ReactNode;
    className?: string;
  }
> = P & {
  /**
   * Component type that will contain the children and the loading indicator
   */
  as?: string | ComponentType<P>;
  /**
   * Whether the component should display in a loading state
   */
  loading: boolean;
  /**
   * Sets one of the 'loading-indicator-overlay-type-' CSS classes.
   */
  overlay: 'main' | 'block' | 'inline-block';
};

/**
 * Displays a loading indicator over an element while the element is in a loading state.
 *
 * When the loading indicator is showing, the wrapped content will be obscured,
 * hidden, or filtered, and the loading indicator will be shown inside the
 * element's current bounding rectangle on top of the content. The geometry of
 * the element will not be affected by the appearance or disappearance of the
 * indicator.
 *
 * You must specify one of several CSS classes to refine the appearance and
 * behavior of the overlay to whatever is most appropriate for its use case.
 * These classes all begin with the prefix **'loading-indicator-overlay-type-'**
 * and end with a specific type name. It is possible for these classes to be
 * specified directly as normal CSS classes using the `className` property, but
 * since at least one is required, the required property `overlay` must be provided
 * a value to indicate which one to use.
 *
 * Allowed type names:
 *
 * - **'main'** - Style for the whole main content area of a page
 * - **'block'** - Style for block-level elements
 * - **'inline-block'** - Style for inline elements composed of blocks
 */
const LoadingIndicatorOverlay = <
  P extends {
    children: ReactNode;
    className?: string;
    style?: CSSProperties;
  }
>({
  as: Component = 'div',
  children,
  className,
  loading,
  overlay,
  ...props
}: Props<P>) => (
  <Component
    {...(props as unknown as P)}
    className={classNames(
      'loading-indicator-overlay',
      { [`loading-indicator-overlay-type-${overlay}`]: !!overlay },
      className,
      !!loading && 'loading-indicator-overlay-loading'
    )}
  >
    <div className="loading-indicator-overlay-children-container">{children}</div>
    <SlidingFixedParent className="loading-indicator-overlay-icon-container">
      <LoadingIndicator as={SlidingFixedChild} />
    </SlidingFixedParent>
  </Component>
);
export default LoadingIndicatorOverlay;
